import * as AddressFormatter from '@shopify/address/build/ts/index';
import axios from 'axios';
import dayjs from 'dayjs';
import Micromodal from 'micromodal';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Setting, Subscription } from '../App';
import { CUSTOMER_API_URL } from '../constants';

type DeliveryDate = {
  current: dayjs.Dayjs;
  change: dayjs.Dayjs;
  min: dayjs.Dayjs;
  max: dayjs.Dayjs;
};

export const useSubscriptionDetail = (setting: Setting) => {
  const [subscription, setSubscription] = useState<Subscription>();
  const { id } = useParams<{ id: string }>();
  const [deliveryDate, setDeliveryDate] = useState<DeliveryDate>();

  // TODO: locale
  const addressFormatter = new AddressFormatter.default('ja');

  useEffect(() => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscription`,
        { subscriptionId: id },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setSubscription({ ...response.data, disabled: false });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (
        !subscription ||
        (subscription.deliveryFormattedAddress && subscription.deliveryFormattedAddress.length !== 0)
      )
        return;

      // Ignore if subscription's delivery address is blank
      if (!subscription.deliveryCountryCode) return;

      const {
        deliveryAddress1,
        deliveryAddress2,
        deliveryCity,
        deliveryCompany,
        deliveryCountryCode,
        deliveryFirstName,
        deliveryLastName,
        deliveryPhone,
        deliveryProvinceCode,
        deliveryZip,
      } = subscription;

      let deliveryFormattedAddress = await addressFormatter.format({
        address1: deliveryAddress1,
        address2: deliveryAddress2,
        city: deliveryCity,
        company: deliveryCompany,
        country: deliveryCountryCode,
        firstName: deliveryFirstName,
        lastName: deliveryLastName,
        phone: deliveryPhone,
        province: deliveryProvinceCode,
        zip: deliveryZip,
      });
      const countries = await addressFormatter.getCountries();
      const country = await addressFormatter.getCountry(deliveryCountryCode);

      //書式の変更が未定なのでコメントアウト
      //deliveryFormattedAddress = sortAddress(country.formatting.show, deliveryFormattedAddress);

      setSubscription({
        ...subscription,
        deliveryFormattedAddress,
        countries,
        country,
      });

      const nextBilling = dayjs(subscription!.nextBillingDate);
      let nextDelivery = nextBilling.add(subscription!.deliveryDays, 'day');

      if (!disableWeek().every((v) => v)) {
        const disableDays = disableWeek().slice(nextDelivery.day()).concat(disableWeek().slice(0, nextDelivery.day()));
        nextDelivery = nextDelivery.add(
          disableDays.findIndex((v) => !v),
          'days'
        );
      }

      setDeliveryDate({
        current: nextDelivery,
        change: nextDelivery,
        min: nextBilling.add(subscription!.shop.deliveryDays, 'day'),
        max: nextBilling.add(30, 'day'),
      });

      // MicroModal の初期化
      Micromodal.init();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  useLayoutEffect(() => {
    const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

    setting.resize(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  const skip = () => {
    setSubscription({ ...subscription!, disabled: true });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=skipSubscription`,
        {
          subscriptionId: id,
        },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          nextBillingDate: new Date(response.data.nextBillingDate),
          disabled: false,
        });

        Micromodal.show('modal-skip-complete');
      });

    Micromodal.close('modal-skip');
  };

  const updateStatusOpen = (status: string) => {
    Micromodal.show(`modal-${status}`);
  };

  const updateStatus = (status: string) => {
    setSubscription({ ...subscription!, disabled: true });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=updateSubscription`,
        {
          subscriptionId: id,
          status,
        },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          status: response.data.status,
          disabled: false,
        });

        Micromodal.show(`modal-${status}-complete`);
      });

    Micromodal.close(`modal-${status}`);
  };

  const disableWeek = () => {
    return [
      subscription!.shop.disableDeliveryDaysSun,
      subscription!.shop.disableDeliveryDaysMon,
      subscription!.shop.disableDeliveryDaysTue,
      subscription!.shop.disableDeliveryDaysWed,
      subscription!.shop.disableDeliveryDaysThr,
      subscription!.shop.disableDeliveryDaysFri,
      subscription!.shop.disableDeliveryDaysSat,
    ];
  };

  const nextDeliveryDateChange = (date: Date) => {
    setDeliveryDate({
      ...deliveryDate!,
      change: dayjs(date),
    });
  };

  const isDisableDays = (date: Date) => {
    return !disableWeek()[date.getDay()];
  };

  const updateDeliveryDays = () => {
    setSubscription({ ...subscription!, disabled: true });

    const days = deliveryDate!.change.diff(dayjs(subscription!.nextBillingDate), 'day');

    axios
      .post(
        `${CUSTOMER_API_URL}?action=updateDeliveryDays`,
        {
          subscriptionId: id,
          deliveryDays: days,
        },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          deliveryDays: response.data.deliveryDays,
          disabled: false,
        });

        setDeliveryDate({
          ...deliveryDate!,
          current: deliveryDate!.change,
        });

        Micromodal.close('modal-delivery-days');
        Micromodal.show('modal-delivery-days-complete');
      });
  };

  /*
  const sortAddress = (format: string, data: string[]): any =>{
    // JP のフォーマット例
    // "{country} 〒{zip}_{province} {city}_{address1}_{address2}_{company}_{lastName} {firstName}様_{phone}"
    // アンダースコア単位で配列が設定されている
    const fmts = format.split('_');

    // 表記の設定として、
    // ・名前
    // ・電話番号
    // ・郵便番号
    // ・住所１
    // ・住所２
    // のデータに変換する
    const fmt_name = fmts.findIndex((v) => v.match(/(lastName|firstName)/));
    const fmt_phone = fmts.findIndex((v) => v.match(/phone/));
    const fmt_zip = fmts.findIndex((v) => v.match(/zip/));
    const fmt_addr1 = fmts.findIndex((v) => v.match(/address1/));
    const fmt_addr2 = fmts.findIndex((v) => v.match(/address2/));

    return [
      fmt_name >= 0 ? data[fmt_name] : '',
      fmt_phone >= 0 ? data[fmt_phone] : '',
      fmt_zip >= 0 ? data[fmt_zip].split(' ')[1] : '',
      fmt_addr1 >= 0 ? data[fmt_addr1] : '',
      fmt_addr2 >= 0 ? data[fmt_addr2] : '',
    ];
  };
  */

  return {
    subscription,
    skip,
    updateStatus,
    deliveryDate,
    nextDeliveryDateChange,
    isDisableDays,
    updateDeliveryDays,
    updateStatusOpen,
  };
};
